/**
 * The Hand Calculator on the first screen (Indra, 2026-10-04): build a hand with the same tile grid used to record a round and see what
 * it would score — for teaching, and for questions BETWEEN games (during a game the app always shows the scoring screen, whose own
 * round form carries the same live preview). Nothing is recorded, saved or sent anywhere, and there is no photo scan: scans use paid
 * recognition credits and this is meant to cost nothing to run.
 *
 * It scores with the house rules this device used last (editable from here) and a Fun / Serious switch, using the same engine, the
 * same preview card and the same extras tick-boxes as the round form, so an answer here is the answer the game would give.
 */

import { newGame } from '../../game';
import type { Game } from '../../game/types';
import { makeSettings, type HkosSettings } from '../../rules/hkos';
import type { ScoringMode } from '../../rules/hkos';
import type { Wind } from '../../rules/tiles';
import { renderBrandHeader } from '../branding';
import { resolveWinningTile } from '../helpers';
import { mountHandPreview } from '../handPreview';
import { seatWindOf } from '../rotation';
import { renderTileGrid } from '../tileGrid';
import { tileGridContextFor } from '../tileGridContext';
import { circumstantialHtml, readCircumstances } from '../winForm';
import type { HouseRulesOverrides } from './settings';

export interface HandCalculatorActions {
  back(): void;
  /** Opens the House Rules editor for this mode; it comes back to the calculator when it is saved or cancelled. */
  openHouseRules(mode: ScoringMode): void;
}

const SEATS = ['East', 'South', 'West', 'North'] as const;
const WINDS: Wind[] = ['east', 'south', 'west', 'north'];

/** Four stand-in players named by their seats, East dealing — the calculator has no table, only a seat and a round wind. */
export function calculatorGame(mode: ScoringMode, rules: HouseRulesOverrides): Game {
  return newGame({
    id: 'calculator',
    tableId: 'calculator',
    rulesetId: 'hkos',
    settings: makeSettings({ mode, toggles: rules.toggles, serious: rules.serious, fun: rules.fun }),
    players: SEATS.map((name, i) => ({ id: `p${i + 1}`, name, seat: WINDS[i]! })),
  });
}

export function rulesLine(mode: ScoringMode, settings: HkosSettings): string {
  const minimum = mode === 'serious' ? settings.serious.minimumFan : settings.fun.minimumFan;
  const cap = mode === 'serious' ? settings.serious.maxFanCap : settings.fun.maxFanCap;
  return `Rules: <strong>${mode === 'serious' ? 'Serious Mode' : 'Fun Mode'}</strong> · min ${minimum} fan · max ${cap} fan · <a href="#" id="calcRulesLink">House Rules…</a>`;
}

export function renderHandCalculator(
  root: HTMLElement,
  params: { mode: ScoringMode; rules: HouseRulesOverrides },
  actions: HandCalculatorActions,
): void {
  let mode = params.mode;
  let game = calculatorGame(mode, params.rules);
  const settings = (): HkosSettings => game.settings as HkosSettings;

  const seatOptions = (selected: number, skip?: number): string =>
    SEATS.map((name, i) => (i === skip ? '' : `<option value="${i}" ${i === selected ? 'selected' : ''}>${name}${i === 0 ? ' (dealer)' : ''}</option>`)).join('');
  const roundOptions = WINDS.map((w, i) => `<option value="${w}" ${i === 0 ? 'selected' : ''}>${SEATS[i]}</option>`).join('');

  root.innerHTML = `
    ${renderBrandHeader()}
    <div class="card">
      <h1>Hand Calculator</h1>
      <span class="calc-banner">Calculator only — nothing is recorded</span>
      <p class="calc-rules" id="calcRules"></p>
      <div class="seg" role="group" aria-label="Scoring mode">
        <button type="button" data-m="fun">Fun</button>
        <button type="button" data-m="serious">Serious</button>
      </div>
    </div>

    <div id="handPreview" class="hand-preview at-top"></div>

    <div class="card">
      <h2 style="margin-top:0">Who won, and how</h2>
      <div class="calc-grid2">
        <div><label for="calcSeat">Winner's seat</label><select id="calcSeat">${seatOptions(0)}</select></div>
        <div><label for="calcRound">Round wind</label><select id="calcRound">${roundOptions}</select></div>
      </div>
      <label class="checkbox-row"><input type="checkbox" id="selfDraw" /> Self-draw (not a discard)</label>
      <div id="discarderField">
        <label for="calcDiscarder">Discarded by</label>
        <select id="calcDiscarder">${seatOptions(1, 0)}</select>
      </div>
      ${circumstantialHtml(settings().toggles)}
      <p class="muted" style="font-size:0.78rem;margin:0.3rem 0 0">Only the extras the House Rules turn on are listed.</p>
    </div>

    <div class="card"><div id="tileGrid"></div></div>

    <div class="row">
      <button type="button" id="calcClear" class="secondary">Start over</button>
      <button type="button" id="calcBack" class="ghost">← Back</button>
    </div>
  `;

  const seatSel = root.querySelector<HTMLSelectElement>('#calcSeat')!;
  const roundSel = root.querySelector<HTMLSelectElement>('#calcRound')!;
  const selfDrawCb = root.querySelector<HTMLInputElement>('#selfDraw')!;
  const discarderField = root.querySelector<HTMLDivElement>('#discarderField')!;
  const discarderSel = root.querySelector<HTMLSelectElement>('#calcDiscarder')!;
  const rulesEl = root.querySelector<HTMLParagraphElement>('#calcRules')!;
  const winnerId = (): string => `p${Number(seatSel.value) + 1}`;

  const gridContext = () =>
    tileGridContextFor(settings(), game.players.map((p) => p.id), 0, roundSel.value as Wind, winnerId());

  let preview: { refresh(): void; setGame(g: Game): void } | undefined;
  const tileGrid = renderTileGrid(root.querySelector<HTMLDivElement>('#tileGrid')!, undefined, gridContext(), {
    onChange: () => preview?.refresh(),
  });

  const paintMode = (): void => {
    for (const b of root.querySelectorAll<HTMLButtonElement>('.seg button')) b.classList.toggle('on', b.dataset['m'] === mode);
    rulesEl.innerHTML = rulesLine(mode, settings());
    rulesEl.querySelector<HTMLAnchorElement>('#calcRulesLink')!.addEventListener('click', (e) => {
      e.preventDefault();
      actions.openHouseRules(mode);
    });
  };

  preview = mountHandPreview(
    root.querySelector<HTMLDivElement>('#handPreview')!,
    game,
    {
      read: () => {
        const seat = Number(seatSel.value);
        const selfDraw = selfDrawCb.checked;
        const circ = readCircumstances(root);
        const { concealed, melds, winningTile: picked, flowers, claimedSet } = tileGrid.read();
        return {
          hand: { concealed, melds, winningTile: resolveWinningTile(picked, concealed, circ.heavenlyHand), flowers, claimedSet },
          ctx: {
            winner: `p${seat + 1}`,
            seatWind: seatWindOf(seat, 0),
            prevailingWind: roundSel.value as Wind,
            dealer: 'p1',
            selfDraw,
            ...(selfDraw ? {} : { discarder: `p${Number(discarderSel.value) + 1}` }),
            ...circ,
          },
        };
      },
    },
    { label: 'Result', explainWaiting: true, startOpen: true },
  );
  paintMode();

  // Fun / Serious: the same hand, scored under the other rules.
  for (const b of root.querySelectorAll<HTMLButtonElement>('.seg button')) {
    b.addEventListener('click', () => {
      mode = b.dataset['m'] as ScoringMode;
      game = calculatorGame(mode, params.rules);
      paintMode();
      preview?.setGame(game);
    });
  }

  // The discarder can be anyone but the winner.
  const fixDiscarder = (): void => {
    const winner = Number(seatSel.value);
    const keep = Number(discarderSel.value);
    const next = keep !== winner ? keep : (winner + 1) % 4;
    discarderSel.innerHTML = seatOptions(next, winner);
  };
  seatSel.addEventListener('change', () => {
    fixDiscarder();
    tileGrid.updateContext(gridContext());
  });
  roundSel.addEventListener('change', () => tileGrid.updateContext(gridContext()));
  selfDrawCb.addEventListener('change', () => {
    discarderField.style.display = selfDrawCb.checked ? 'none' : '';
  });
  for (const el of [seatSel, roundSel, selfDrawCb, discarderSel]) el.addEventListener('change', () => preview?.refresh());
  root.querySelectorAll<HTMLInputElement>('.checkbox-row input').forEach((el) => el.addEventListener('change', () => preview?.refresh()));

  root.querySelector<HTMLButtonElement>('#calcClear')!.addEventListener('click', () => {
    renderHandCalculator(root, { mode, rules: params.rules }, actions);
  });
  root.querySelector<HTMLButtonElement>('#calcBack')!.addEventListener('click', () => actions.back());
}
